import { useEffect, useState } from "react";
import TimeAgo from "react-timeago";
import { useSystemStatus } from "@/app/hooks/useSystemStatus";
import { cn } from "@/app/utils";

interface SystemStatusProps {
  className?: string;
}

export function SystemStatus({ className }: SystemStatusProps) {
  const { status, lastChecked, launchedAt, lastLaunchedAt, checkHealth } = useSystemStatus();
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every second for dynamic uptime
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const getUptimeComponent = () => {
    if (!launchedAt) return null;
    return (
      <TimeAgo
        date={launchedAt}
        live={true}
        minPeriod={1}
        formatter={(value: number, unit: string) => `${value}${unit.charAt(0)}`}
      />
    );
  };

  const formatTooltip = (): string => {
    const parts: string[] = [];

    parts.push(getStatusText());

    if (launchedAt) {
      parts.push(`Started: ${launchedAt.toLocaleString()}`);
      const uptimeMs = currentTime.getTime() - launchedAt.getTime();
      parts.push(`Uptime: ${Math.floor(uptimeMs / 1000)}s`);
    }

    if (lastChecked) {
      parts.push(`Last checked: ${lastChecked.toLocaleString()}`);
    }

    if (lastLaunchedAt) {
      parts.push(`Previous start: ${lastLaunchedAt.toLocaleString()}`);
    }

    return parts.join(" • ");
  };

  const getStatusColor = (): string => {
    switch (status) {
      case "healthy":
        return "text-green-500";
      case "unhealthy":
        return "text-red-500";
      case "checking":
        return "text-yellow-500";
    }
  };

  const getStatusIcon = (): string => {
    switch (status) {
      case "healthy":
        return "●";
      case "unhealthy":
        return "●";
      case "checking":
        return "◐";
    }
  };

  const getStatusText = (): string => {
    switch (status) {
      case "healthy":
        return "System Healthy";
      case "unhealthy":
        return "System Issues";
      case "checking":
        return "Checking...";
    }
  };

  const handleClick = (): void => {
    void checkHealth();
  };

  const statusColor = getStatusColor();
  const statusIcon = getStatusIcon();
  const statusText = getStatusText();

  const uptimeComponent = getUptimeComponent();

  return (
    <div className={cn("flex items-center gap-2 text-sm", className)}>
      <div
        className="flex items-center gap-2 cursor-pointer"
        onClick={handleClick}
        title={formatTooltip()}
      >
        <span className={cn("animate-pulse", statusColor)}>{statusIcon}</span>
        <span className={cn("text-xs", statusColor)}>{statusText}</span>
        {status === "healthy" && uptimeComponent && (
          <span className={cn("text-xs opacity-60", statusColor)}>• {uptimeComponent}</span>
        )}
      </div>
    </div>
  );
}
